import { useMemo } from "react";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { WorkflowType } from "@/pages/query/types";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { GET_DBT_GRAPH_DATA, GET_DBT_JOBS_LIST } from "@/pages/query/constants";

export const useDbtGraphFilters = (aggregateBy: "day" | "month" | "year") => {
  const { getFilterValue } = useDatatableContext();
  const { startDate, endDate } = useDateFromSearchParams();

  const teamsFilter = useMemo(() => getFilterValue<string[]>("teams") || [], [getFilterValue]);
  const appsFilter = useMemo(() => getFilterValue<string[]>("apps") || [], [getFilterValue]);
  const workflowFilter = useMemo(
    () => getFilterValue<string[]>("workflows") || [],
    [getFilterValue]
  );
  const warehouseFilter = useMemo(
    () => getFilterValue<string[]>("warehouses") || [],
    [getFilterValue]
  );
  const integrationNameFilter = useMemo(
    () => getFilterValue<string[]>("integration_names") || [],
    [getFilterValue]
  );
  const integrationEnvironmentFilter = useMemo(
    () => getFilterValue<string[]>("integration_environments") || [],
    [getFilterValue]
  );

  const filterDeps = [
    ...workflowFilter,
    ...warehouseFilter,
    ...teamsFilter,
    ...appsFilter,
    ...integrationNameFilter,
    ...integrationEnvironmentFilter,
  ];

  const graphParams = {
    start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
    end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
    workflows: workflowFilter,
    warehouses: warehouseFilter,
    teams: teamsFilter,
    apps: appsFilter,
    integration_names: integrationNameFilter,
    integration_environments: integrationEnvironmentFilter,
    workflow_type: WorkflowType.DBT,
    aggregate_date_interval: aggregateBy,
  };

  const graphSummaryQueryKey = [
    GET_DBT_JOBS_LIST,
    "dbt_jobs_graph_summary",
    startDate,
    endDate,
    aggregateBy,
    ...filterDeps,
  ];

  const graphDataQueryKey = [
    GET_DBT_JOBS_LIST,
    GET_DBT_GRAPH_DATA,
    startDate,
    endDate,
    aggregateBy,
    ...filterDeps,
  ];

  return { startDate, endDate, graphParams, graphSummaryQueryKey, graphDataQueryKey };
};
